import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EllipsisIcon,
} from '@rsos/assets/icons';
import styles from './Pagination.module.scss';

interface PaginationProps {
  page: number;
  totalPages: number;
  total: number;
  shownCount: number;
  pageSize: number;
  pageSizeOptions: number[];
  onPageSizeChange: (size: number) => void;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}

const ELLIPSIS = 'ellipsis';
type PageItem = number | typeof ELLIPSIS;

const range = (start: number, end: number): number[] =>
  Array.from({ length: end - start + 1 }, (_, i) => start + i);

// Builds the list of page buttons, collapsing long runs to an ellipsis while
// always keeping the first and last page visible alongside the current page's
// immediate neighbors — e.g. `1 … 4 5 6 … 20`.
const buildPageItems = (page: number, totalPages: number): PageItem[] => {
  const siblings = 1;
  // first + last + current + 2 siblings + 2 ellipses
  const maxButtons = siblings * 2 + 5;

  if (totalPages <= maxButtons) {
    return range(1, totalPages);
  }

  const leftSibling = Math.max(page - siblings, 1);
  const rightSibling = Math.min(page + siblings, totalPages);
  const showLeftEllipsis = leftSibling > 3;
  const showRightEllipsis = rightSibling < totalPages - 2;
  const edgeCount = 3 + siblings * 2;

  if (!showLeftEllipsis && showRightEllipsis) {
    return [...range(1, edgeCount), ELLIPSIS, totalPages];
  }
  if (showLeftEllipsis && !showRightEllipsis) {
    return [1, ELLIPSIS, ...range(totalPages - edgeCount + 1, totalPages)];
  }

  return [
    1,
    ELLIPSIS,
    ...range(leftSibling, rightSibling),
    ELLIPSIS,
    totalPages,
  ];
};

const Pagination = ({
  page,
  totalPages,
  total,
  shownCount,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  onPageChange,
  disabled = false,
}: PaginationProps) => {
  if (total === 0) {
    return null;
  }

  const pageItems = buildPageItems(page, totalPages);

  // 1-based range of the rows on screen. `shownCount` (the actual rendered row
  // count) makes the upper bound exact on a partial last page.
  const rangeStart = (page - 1) * pageSize + 1;
  const rangeEnd = rangeStart + shownCount - 1;

  return (
    <div className={styles.pagination}>
      <label className={styles.pageSize}>
        <span>Conversations per page</span>
        <select
          className={styles.pageSizeSelect}
          value={pageSize}
          onChange={e => onPageSizeChange(Number(e.target.value))}
          disabled={disabled}
          aria-label="Conversations per page"
        >
          {pageSizeOptions.map(size => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </label>
      <span className={styles.count} data-name="conversation-pagination-count">
        Showing {rangeStart}–{rangeEnd} of {total} conversations
      </span>
      <div className={styles.controls}>
        <button
          className={`${styles.pageBtn} ${styles.navBtn}`}
          onClick={() => onPageChange(page - 1)}
          disabled={disabled || page <= 1}
          aria-label="Previous page"
        >
          <ChevronLeftIcon
            width={10}
            height={10}
            viewboxWidth={17}
            viewboxHeight={17}
          />
        </button>
        {pageItems.map((item, index) =>
          item === ELLIPSIS ? (
            <span
              key={`ellipsis-${index}`}
              className={styles.ellipsis}
              data-testid="page-ellipsis"
              aria-hidden="true"
            >
              <EllipsisIcon width={14} height={14} />
            </span>
          ) : (
            <button
              key={item}
              className={`${styles.pageBtn}${item === page ? ` ${styles.pageBtnActive}` : ''}`}
              onClick={() => onPageChange(item)}
              disabled={disabled}
              aria-label={`Page ${item}`}
              aria-current={item === page ? 'page' : undefined}
            >
              {item}
            </button>
          ),
        )}
        <button
          className={`${styles.pageBtn} ${styles.navBtn}`}
          onClick={() => onPageChange(page + 1)}
          disabled={disabled || page >= totalPages}
          aria-label="Next page"
        >
          <ChevronRightIcon
            width={10}
            height={10}
            viewboxWidth={17}
            viewboxHeight={17}
          />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
